import { CalendarDay } from '../utils/calendarGrid';
import { Availability, AvailabilityDaySourceAttribution } from '../types/availability';
import { AvailabilityGapMarker } from '../utils/availabilityGaps';
import { usePopover } from '../hooks/usePopover';
import { AvailabilityPopover } from './AvailabilityPopover';
import { useTranslation } from 'react-i18next';
import { buildAvailabilitySourceBadge, categorizeAvailabilityReason } from '../utils/availabilitySourceBadges';

interface CalendarDayCellProps {
  readonly day: CalendarDay;
  readonly availability: Availability | undefined;
  readonly gapMarker?: AvailabilityGapMarker;
  readonly daySourceAttribution?: AvailabilityDaySourceAttribution;
  readonly onEdit: (availability: Availability) => void;
  readonly onBlock: (id: string) => void;
  readonly onUnblock: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onAddFromDay: (isoDate: string) => void;
  readonly isMutating: boolean;
}

const getStatusColorClass = (status: Availability['status']): string => {
  switch (status) {
    case 'AVAILABLE':
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    case 'BLOCKED':
      return 'bg-red-100 text-red-800 hover:bg-red-200';
    case 'BOOKED':
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
  }
};

// Diagonal half-cell overlay split along the top-left/bottom-right axis: 'gap-after'
// (checkout day) shades the upper-right half to signal the uncovered night ahead of it;
// 'gap-before' (check-in day) shades the lower-left half to signal the uncovered night behind it.
const GAP_CLIP_PATH: Record<AvailabilityGapMarker, string> = {
  'gap-after': 'polygon(100% 0, 100% 100%, 0 0)',
  'gap-before': 'polygon(0 0, 0 100%, 100% 100%)',
};

export const CalendarDayCell = ({
  day,
  availability,
  gapMarker,
  daySourceAttribution,
  onEdit,
  onBlock,
  onUnblock,
  onRemove,
  onAddFromDay,
  isMutating,
}: CalendarDayCellProps) => {
  const { t } = useTranslation(['pages']);
  const { open, openAbove, triggerRef, popoverRef, toggle, close } = usePopover();
  const sourceBadge = daySourceAttribution
    ? buildAvailabilitySourceBadge(daySourceAttribution, {
        sourceAirbnb: t('pages:icalFeedSourceAirbnb'),
        sourceBooking: t('pages:icalFeedSourceBooking'),
        sourceExpedia: t('pages:icalFeedSourceExpedia'),
        sourceFewoDirekt: t('pages:icalFeedSourceFewoDirekt', 'FeWo-direkt'),
        sourceVrbo: t('pages:icalFeedSourceVrbo', 'Vrbo'),
        sourceTripadvisor: t('pages:icalFeedSourceTripadvisor', 'TripAdvisor'),
        sourceOther: t('pages:icalFeedSourceOther'),
        sourceBadge: t('pages:availabilitySourceBadge'),
        formatMultipleSources: (source, count) =>
          t('pages:availabilitySourceMultiple', { source, count }),
      })
    : null;

  // Padding cell: non-current-month → muted, non-interactive
  if (!day.isCurrentMonth) {
    return (
      <div
        data-status="none"
        className="relative p-1 min-h-[2.5rem] flex items-center justify-center text-gray-300 text-sm select-none"
        aria-hidden="true"
      >
        <span>{day.day}</span>
      </div>
    );
  }

  const dataStatus = availability?.status ?? 'none';
  const reasonCategory = availability ? categorizeAvailabilityReason(availability) : null;
  const dataReason = reasonCategory?.kind ?? 'none';

  if (availability) {
    // Coloured cell — opens popover on click
    const colorClass = getStatusColorClass(availability.status);
    const gapTitle = gapMarker
      ? t(gapMarker === 'gap-after' ? 'pages:availabilityGapAfter' : 'pages:availabilityGapBefore')
      : undefined;
    return (
      <div className="relative p-1">
        <button
          ref={triggerRef}
          data-status={dataStatus}
          data-reason={dataReason}
          aria-current={day.isToday ? 'date' : undefined}
          onClick={toggle}
          className={`relative overflow-hidden w-full min-h-[2.5rem] rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${colorClass} ${
            day.isToday ? 'ring-2 ring-offset-1 ring-current' : ''
          }`}
        >
          {gapMarker && (
            <span
              data-testid="availability-gap-marker"
              data-gap={gapMarker}
              title={gapTitle}
              aria-label={gapTitle}
              className="absolute inset-0 rounded-lg bg-amber-500/60 pointer-events-none"
              style={{ clipPath: GAP_CLIP_PATH[gapMarker] }}
            />
          )}
          <div className="flex flex-col items-center leading-tight gap-0.5">
            <span>{day.day}</span>
            {sourceBadge && (
              <span
                className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700 border border-blue-200"
                aria-label={sourceBadge.ariaLabel}
              >
                {sourceBadge.label}
              </span>
            )}
          </div>
        </button>

        {open && (
          <div ref={popoverRef}>
            <AvailabilityPopover
              availability={availability}
              sourceBadgeLabel={sourceBadge?.label}
              onClose={close}
              onEdit={(av) => { close(); onEdit(av); }}
              onBlock={(id) => { close(); onBlock(id); }}
              onUnblock={(id) => { close(); onUnblock(id); }}
              onRemove={(id) => { close(); onRemove(id); }}
              isMutating={isMutating}
              openAbove={openAbove}
            />
          </div>
        )}
      </div>
    );
  }

  // Neutral cell — uncovered day: clicking opens Add modal pre-filled
  return (
    <div className="relative p-1">
      <button
        data-status="none"
        aria-current={day.isToday ? 'date' : undefined}
        onClick={() => onAddFromDay(day.iso)}
        className={`w-full min-h-[2.5rem] rounded-lg flex items-center justify-center text-sm text-gray-700 hover:bg-gray-50 transition-colors border border-dashed border-gray-200 ${
          day.isToday ? 'ring-2 ring-offset-1 ring-primary-500 font-semibold' : ''
        }`}
      >
        <div className="flex flex-col items-center leading-tight gap-0.5">
          <span>{day.day}</span>
          {sourceBadge && (
            <span
              className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700 border border-blue-200"
              aria-label={sourceBadge.ariaLabel}
            >
              {sourceBadge.label}
            </span>
          )}
        </div>
      </button>
    </div>
  );
};
